
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Terms() {
  const [activeSection, setActiveSection] = useState('acceptance');
  const navigate = useNavigate();

  const sections = [
    {
      id: 'acceptance',
      icon: 'ri-check-line',
      title: 'Acceptance of Terms',
      subtitle: 'Agreement to Terms',
      content: `By accessing and using GalaxyFabTech's website and services, you accept and agree to be bound by the terms and provision of this agreement.
      
      These terms apply to all users of the website, including without limitation users who are:
      • Visitors browsing the website
      • Customers purchasing services
      • Partners and distributors
      • Vendors and suppliers
      
      If you do not agree to abide by the above, please do not use this service. We reserve the right to change these terms at any time without prior notice.`
    },
    {
      id: 'services',
      icon: 'ri-settings-4-line',
      title: 'Services Description',
      subtitle: 'What We Provide',
      content: `GalaxyFabTech provides commercial laundry equipment solutions including:
      
      • Equipment consultation and planning services
      • Installation and commissioning services
      • Maintenance and repair services
      • Training and technical support
      • Spare parts supply and replacement
      • Project management and implementation
      
      All services are subject to availability, technical feasibility, and commercial viability. We reserve the right to modify or discontinue services with reasonable notice.`
    },
    {
      id: 'user-obligations',
      icon: 'ri-user-settings-line',
      title: 'User Obligations',
      subtitle: 'Your Responsibilities',
      content: `As a user of our services, you agree to:
      
      • Provide accurate and complete information
      • Use services only for lawful purposes
      • Respect intellectual property rights
      • Maintain confidentiality of account information
      • Comply with all applicable laws and regulations
      • Not interfere with website operation or security
      
      You are responsible for all activities that occur under your account and for maintaining the security of your login credentials.`
    },
    {
      id: 'payment-terms',
      icon: 'ri-bank-card-line',
      title: 'Payment Terms',
      subtitle: 'Billing and Payment',
      content: `Payment terms for services are as follows:
      
      • Consultation services may be provided free or at quoted rates
      • Equipment purchases require advance payment or financing
      • Service contracts are billed according to agreed schedules
      • Late payments may incur additional charges
      • All prices are subject to applicable taxes
      • Payment disputes must be raised within 30 days
      
      We accept various payment methods including bank transfers, checks, and financing arrangements. All payments are processed securely.`
    },
    {
      id: 'warranties',
      icon: 'ri-shield-check-line',
      title: 'Warranties & Disclaimers',
      subtitle: 'Service Guarantees',
      content: `Equipment and Service Warranties:
      
      • Equipment warranties as per manufacturer specifications
      • Installation workmanship warranty for 1 year
      • Service quality guarantee with satisfaction assurance
      • Parts and labor warranties on repairs
      • Training effectiveness and knowledge transfer guarantee
      
      DISCLAIMERS: Services are provided "as is" except where expressly warranted. We disclaim all other warranties, express or implied, including merchantability and fitness for particular purpose.`
    },
    {
      id: 'liability',
      icon: 'ri-error-warning-line',
      title: 'Limitation of Liability',
      subtitle: 'Liability Limits',
      content: `Our liability is limited as follows:
      
      • Maximum liability limited to service contract value
      • No liability for indirect, consequential, or punitive damages
      • No liability for business interruption, or lost profits
      • Force majeure events exempt from liability
      • User negligence or misuse voids liability claims
      • Equipment manufacturer defects covered by manufacturer
      
      These limitations apply to the fullest extent permitted by law. Some jurisdictions may not allow certain limitations.`
    }
  ];

  const currentSection = sections.find(section => section.id === activeSection);
  const currentIndex = sections.findIndex(section => section.id === activeSection);
  const isLastSection = currentIndex === sections.length - 1;

  const handleNextSection = () => {
    if (currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
    }
  };

  const handleHaveQuestions = () => {
    navigate('/contact');
  };

  const handleContactUs = () => {
    navigate('/contact');
  };

  const handleAboutUs = () => {
    navigate('/about');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section - Aligned with Services Page Design */}
      <section className="pt-32 pb-20 bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-5"
            style={{
              backgroundImage: `url('/images/backgrounds/terms-hero-bg.jpg')`
            }}
          ></div>
        </div>

        {/* Clean Floating Elements */}
        <div className="absolute top-20 left-10 w-16 h-16 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-32 right-20 w-12 h-12 bg-teal-100 rounded-full opacity-25 animate-bounce"></div>
        <div className="absolute bottom-20 left-1/4 w-10 h-10 bg-gray-100 rounded-full opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 right-10 w-8 h-8 bg-blue-100 rounded-full opacity-30 animate-bounce" style={{ animationDelay: '2s' }}></div>

        {/* Simple Icon Elements */}
        <div className="absolute top-40 left-1/3 w-12 h-12 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-sm animate-float">
          <i className="ri-file-text-line text-blue-500 text-lg"></i>
        </div>
        <div className="absolute bottom-32 right-1/3 w-12 h-12 bg-white border border-gray-200 rounded-lg flex items-center justify-center shadow-sm animate-float" style={{ animationDelay: '1.5s' }}>
          <i className="ri-scales-line text-teal-500 text-lg"></i>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-flex items-center px-6 py-3 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-sm font-semibold shadow-sm">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-file-text-line text-sm text-white"></i>
                </div>
                Legal Terms & Conditions
                <span className="w-2 h-2 bg-blue-500 rounded-full ml-3"></span>
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight">
              Terms &{' '}
              <span className="text-blue-600">
                Conditions
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Please read these terms and conditions carefully before using our services. These terms govern your use of our website and services.
            </p>
          </div>
        </div>

        {/* Custom CSS for floating animation */}
        <style jsx>{`
          @keyframes animate-float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-10px) rotate(5deg);
            }
          }
          .animate-float {
            animation: animate-float 3s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Action Buttons */}
      <section className="py-8 bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Buttons removed as requested */}
        </div>
      </section>

      {/* Terms Sections Navigation */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-white shadow-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {sections.map((section) => (
              <div key={section.id} className="group/btn relative">
                <div className={`absolute inset-0 ${section.id === activeSection ? 'bg-blue-700' : 'bg-gray-300'} rounded-full transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300`}></div>
                <button
                  onClick={() => setActiveSection(section.id)}
                  className={`relative ${
                    activeSection === section.id
                      ? 'bg-blue-600 text-white border-white/20'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                  } font-medium px-6 py-3 rounded-full transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl whitespace-nowrap border-2`}
                >
                  <i className={`${section.icon} mr-2`}></i>
                  {section.title}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Active Section Detail */}
      {currentSection && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mr-4 shadow-lg">
                    <i className={`${currentSection.icon} text-white text-2xl`}></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">{currentSection.title}</h2>
                    <p className="text-blue-600 font-medium">{currentSection.subtitle}</p>
                  </div>
                </div>
                
                <div className="prose prose-lg text-gray-700 leading-relaxed whitespace-pre-line">
                  {currentSection.content}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 mt-8">
                  <div className="group/btn relative">
                    <div className="absolute inset-0 bg-blue-700 rounded-2xl transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300"></div>
                    <button 
                      onClick={handleHaveQuestions}
                      className="relative bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl whitespace-nowrap border-2 border-white/20"
                    >
                      <span className="flex items-center">
                        <i className="ri-question-line mr-2"></i>
                        Need Clarification?
                      </span>
                    </button>
                  </div>
                  {!isLastSection && (
                    <div className="group/btn relative">
                      <div className="absolute inset-0 bg-gray-300 rounded-2xl transform translate-x-1 translate-y-1 group-hover/btn:translate-x-2 group-hover/btn:translate-y-2 transition-transform duration-300"></div>
                      <button 
                        onClick={handleNextSection}
                        className="relative bg-white border-2 border-gray-300 hover:bg-gray-50 text-gray-800 font-bold px-6 py-3 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl whitespace-nowrap"
                      >
                        <span className="flex items-center">
                          <i className="ri-arrow-right-line mr-2"></i>
                          Next Section
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="/images/terms/terms-illustration.jpg"
                    alt={currentSection.title}
                    className="w-full h-[400px] object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/10"></div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Important Terms Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-teal-50 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/30 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-teal-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-purple-200/30 rounded-full blur-xl animate-pulse delay-500"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Important Terms
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Key terms and conditions that govern your relationship with GalaxyFabTech and our services.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              {
                icon: 'ri-time-line',
                title: 'Service Timeline',
                description: 'All service timelines are estimates and subject to technical requirements, site conditions, and equipment availability.',
                color: 'blue'
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Pricing Terms',
                description: 'All prices are subject to change without notice. Final pricing confirmed in written quotations or contracts.',
                color: 'teal'
              },
              {
                icon: 'ri-tools-line',
                title: 'Equipment Conditions',
                description: 'Equipment warranties are provided by manufacturers. Installation and service warranties are separate and additional.',
                color: 'purple'
              },
              {
                icon: 'ri-user-settings-line',
                title: 'User Responsibilities',
                description: 'Users must provide accurate information, safe working conditions, and comply with all safety requirements.',
                color: 'green'
              },
              {
                icon: 'ri-alert-line',
                title: 'Force Majeure',
                description: 'We are not liable for delays or failures due to circumstances beyond our reasonable control.',
                color: 'orange'
              },
              {
                icon: 'ri-scales-line',
                title: 'Dispute Resolution',
                description: 'All disputes subject to arbitration under Indian law with jurisdiction in Bangalore courts.',
                color: 'indigo'
              }
            ].map((term, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-4 border border-white/20 relative overflow-hidden h-full transform hover:scale-105">
                  {/* Card Background Elements */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-400/20 to-blue-600/10 rounded-full blur-xl"></div>
                  <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full"></div>

                  {/* Floating Particle Effects */}
                  <div className="absolute top-2 right-2 w-2 h-2 bg-blue-400/40 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div
                    className="absolute bottom-2 left-2 w-1 h-1 bg-teal-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700"
                    style={{ animationDelay: '0.5s' }}
                  ></div>

                  <div className="relative z-10">
                    {/* 3D Icon Container */}
                    <div className="relative mb-6">
                      <div className={`w-20 h-20 bg-gradient-to-br ${
                        term.color === 'blue' ? 'from-blue-500 to-blue-600' :
                        term.color === 'teal' ? 'from-teal-500 to-teal-600' :
                        term.color === 'purple' ? 'from-purple-500 to-purple-600' :
                        term.color === 'green' ? 'from-green-500 to-green-600' :
                        term.color === 'orange' ? 'from-orange-500 to-orange-600' :
                        'from-indigo-500 to-indigo-600'
                      } rounded-3xl flex items-center justify-center shadow-2xl group-hover:shadow-3xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 relative`}>
                        {/* 3D Shadow Effect */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${
                          term.color === 'blue' ? 'from-blue-600 to-blue-700' :
                          term.color === 'teal' ? 'from-teal-600 to-teal-700' :
                          term.color === 'purple' ? 'from-purple-600 to-purple-700' :
                          term.color === 'green' ? 'from-green-600 to-green-700' :
                          term.color === 'orange' ? 'from-orange-600 to-orange-700' :
                          'from-indigo-600 to-indigo-700'
                        } rounded-3xl transform translate-x-1 translate-y-1 -z-10 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500`}></div>
                        <i className={`${term.icon} text-white text-3xl group-hover:scale-110 transition-transform duration-300`}></i>

                        {/* Icon Glow Effect */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${
                          term.color === 'blue' ? 'from-blue-400/50 to-blue-400/50' :
                          term.color === 'teal' ? 'from-teal-400/50 to-teal-400/50' :
                          term.color === 'purple' ? 'from-purple-400/50 to-purple-400/50' :
                          term.color === 'green' ? 'from-green-400/50 to-green-400/50' :
                          term.color === 'orange' ? 'from-orange-400/50 to-orange-400/50' :
                          'from-indigo-400/50 to-indigo-400/50'
                        } rounded-3xl opacity-0 group-hover:opacity-100 group-hover:scale-125 blur-xl transition-all duration-500`}></div>
                      </div>

                      {/* Floating Ring */}
                      <div className="absolute inset-0 w-20 h-20 border-2 border-blue-300/30 rounded-3xl group-hover:scale-150 group-hover:border-blue-400/20 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                    </div>

                    <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-blue-700 transition-colors duration-300">
                      {term.title}
                    </h3>

                    {/* Animated Underline */}
                    <div className={`w-12 h-1 bg-gradient-to-r ${
                      term.color === 'blue' ? 'from-blue-500 to-blue-600' :
                      term.color === 'teal' ? 'from-teal-500 to-teal-600' :
                      term.color === 'purple' ? 'from-purple-500 to-purple-600' :
                      term.color === 'green' ? 'from-green-500 to-green-600' :
                      term.color === 'orange' ? 'from-orange-500 to-orange-600' :
                      'from-indigo-500 to-indigo-600'
                    } rounded-full mb-6 group-hover:w-20 transition-all duration-500 shadow-lg`}></div>

                    <p className="text-gray-600 leading-relaxed text-lg group-hover:text-gray-700 transition-colors duration-300">
                      {term.description}
                    </p>

                    {/* Interactive Progress Bar */}
                    <div className="mt-8 h-2 bg-gray-200/50 rounded-full overflow-hidden">
                      <div className={`h-full bg-gradient-to-r ${
                        term.color === 'blue' ? 'from-blue-500 to-blue-600' :
                        term.color === 'teal' ? 'from-teal-500 to-teal-600' :
                        term.color === 'purple' ? 'from-purple-500 to-purple-600' :
                        term.color === 'green' ? 'from-green-500 to-green-600' :
                        term.color === 'orange' ? 'from-orange-500 to-orange-600' :
                        'from-indigo-500 to-indigo-600'
                      } rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 shadow-lg`}></div>
                    </div>

                    {/* Hover Arrow Indicator */}
                    <div className="absolute bottom-6 right-6 w-10 h-10 bg-gradient-to-br from-blue-500/20 to-teal-500/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500">
                      <i className="ri-arrow-up-right-line text-blue-600 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                    </div>
                  </div>

                  {/* Reflection Effect */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-700/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Questions About Our Terms?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact our legal team for clarification on any terms and conditions or service agreements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="group/btn relative">
              <div className="absolute inset-0 bg-white/30 rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-300"></div>
              <button 
                onClick={handleContactUs}
                className="relative bg-white text-blue-600 hover:bg-gray-50 font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-2xl hover:shadow-3xl whitespace-nowrap border-2 border-white/20"
              >
                <span className="flex items-center">
                  <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-mail-line text-sm text-blue-600"></i>
                  </div>
                  Contact Us
                </span>
              </button>
            </div>
            <div className="group/btn relative">
              <div className="absolute inset-0 bg-white/30 rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-300"></div>
              <button 
                onClick={handleAboutUs}
                className="relative bg-white/10 border-2 border-white/30 hover:bg-white/20 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-300 transform hover:-translate-y-1 shadow-2xl hover:shadow-3xl whitespace-nowrap"
              >
                <span className="flex items-center">
                  <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                    <i className="ri-user-line text-sm"></i>
                  </div>
                  About Us
                </span>
              </button>
            </div>
          </div>
          
          <div className="mt-8 text-blue-100 text-sm">
            Last updated: January 2025 | Effective Date: January 1, 2025
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
